/**
 * Discount Modal
 * Allows applying discount to a specific item or the entire sale.
 * Requests manager password when discount exceeds configurable limit.
 */
import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Percent, DollarSign, Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

interface DiscountModalProps {
  isOpen: boolean;
  saleId: string | null;
  saleSubtotal: number;
  selectedItem?: {
    id: string;
    product_name: string;
    subtotal: number;
  } | null;
  onClose: () => void;
  onSuccess: (data: { discount_amount: number; new_total: number }) => void;
}

export function DiscountModal({
  isOpen,
  saleId,
  saleSubtotal,
  selectedItem,
  onClose,
  onSuccess,
}: DiscountModalProps) {
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [managerPassword, setManagerPassword] = useState('');
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const resetForm = useCallback(() => {
    setDiscountType('percentage');
    setValue('');
    setReason('');
    setManagerPassword('');
    setRequiresAuth(false);
    setLoading(false);
    setAuthError('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleApply = useCallback(async () => {
    if (!saleId) {
      toast.error('Nenhuma venda em andamento');
      return;
    }

    const numValue = parseFloat(value);
    if (!numValue || numValue <= 0) {
      toast.error('Informe um valor válido para o desconto');
      return;
    }

    setLoading(true);
    setAuthError('');

    try {
      const endpoint = selectedItem
        ? `/api/sales/${saleId}/items/${selectedItem.id}/discount`
        : `/api/sales/${saleId}/discount`;

      const body: Record<string, unknown> = {
        type: discountType,
        value: numValue,
        reason: reason || undefined,
      };

      if (managerPassword) {
        body.manager_password = managerPassword;
      }

      const response = await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.status === 403 && data.requires_manager_auth) {
        setRequiresAuth(true);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        if (data.error?.includes('Senha do gerente')) {
          setAuthError('Senha do gerente inválida');
          setLoading(false);
          return;
        }
        toast.error(data.error || 'Erro ao aplicar desconto');
        setLoading(false);
        return;
      }

      toast.success(
        `Desconto de R$${data.discount_amount.toFixed(2)} aplicado${selectedItem ? ` em ${selectedItem.product_name}` : ''}`,
      );

      onSuccess({
        discount_amount: data.discount_amount,
        new_total: selectedItem ? data.sale?.total : data.new_total,
      });
      handleClose();
    } catch {
      toast.error('Erro de conexão');
      setLoading(false);
    }
  }, [saleId, selectedItem, discountType, value, reason, managerPassword, onSuccess, handleClose]);

  const formatCurrency = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const targetValue = selectedItem ? selectedItem.subtotal : saleSubtotal;
  const previewDiscount = (() => {
    const numValue = parseFloat(value) || 0;
    if (discountType === 'percentage') {
      return Math.round(targetValue * (numValue / 100) * 100) / 100;
    }
    return numValue;
  })();

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
          >
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 rounded-sm p-1 text-muted-foreground hover:text-foreground"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>

            <h2 className="text-lg font-semibold">
              {selectedItem ? `Desconto: ${selectedItem.product_name}` : 'Desconto na Venda'}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {selectedItem
                ? `Subtotal do item: ${formatCurrency(selectedItem.subtotal)}`
                : `Subtotal da venda: ${formatCurrency(saleSubtotal)}`}
            </p>

            <div className="mt-5 space-y-4">
              {/* Discount Type */}
              <div className="flex gap-2">
                <button
                  onClick={() => setDiscountType('percentage')}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                    discountType === 'percentage'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <Percent className="h-4 w-4" />
                  Percentual
                </button>
                <button
                  onClick={() => setDiscountType('fixed')}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                    discountType === 'fixed'
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <DollarSign className="h-4 w-4" />
                  Valor Fixo
                </button>
              </div>

              {/* Value Input */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  {discountType === 'percentage' ? 'Percentual (%)' : 'Valor (R$)'}
                </label>
                <input
                  type="number"
                  step={discountType === 'percentage' ? '0.1' : '0.01'}
                  min="0"
                  max={discountType === 'percentage' ? '100' : String(targetValue)}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={discountType === 'percentage' ? 'Ex: 5' : 'Ex: 10.00'}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  autoFocus
                />
              </div>

              {/* Preview */}
              {parseFloat(value) > 0 && (
                <div className="rounded-lg border border-border/50 bg-muted/30 p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Valor original:</span>
                    <span>{formatCurrency(targetValue)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-destructive">
                    <span>Desconto:</span>
                    <span>-{formatCurrency(previewDiscount)}</span>
                  </div>
                  <div className="mt-1 flex justify-between border-t border-border/50 pt-1 text-sm font-semibold">
                    <span>Valor final:</span>
                    <span className="text-primary">
                      {formatCurrency(Math.max(0, targetValue - previewDiscount))}
                    </span>
                  </div>
                </div>
              )}

              {/* Reason */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">
                  Motivo (opcional)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: Cliente fidelidade, promoção..."
                  className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>

              {/* Manager Auth */}
              {requiresAuth && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-2"
                >
                  <div className="flex items-center gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-2.5 text-sm text-yellow-500">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>Desconto excede o limite. Autorização do gerente necessária.</span>
                  </div>
                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-foreground">
                      <Lock className="h-3.5 w-3.5" />
                      Senha do Gerente
                    </label>
                    <input
                      type="password"
                      value={managerPassword}
                      onChange={(e) => {
                        setManagerPassword(e.target.value);
                        setAuthError('');
                      }}
                      placeholder="Digite a senha do gerente"
                      className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    {authError && (
                      <p className="mt-1 text-xs text-destructive">{authError}</p>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleClose}
                  className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleApply}
                  disabled={loading || !value || parseFloat(value) <= 0}
                  className="flex-1 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Aplicando...' : 'Aplicar Desconto'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
